import type { RecruitingTeamFitBasis } from "./design-codec";
import { normaliseResultTeams } from "./normalise-result-teams";
import { Fragment, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Button } from "@/components/design/Button";
import { FloatingActionRow } from "./FloatingActionRow";
import { Disclosure } from "@/components/design/Disclosure";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { formatDate } from "@/lib/format-date";
import { userErrorMessage } from "@/lib/user-error";
import { DEFAULT_POWER_SCORE_LENS, POWER_SCORE_LENS_LABEL_IDS, type PowerScoreLens } from "@/lib/score-source";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { SaveMatchModal } from "./MatchModals";
import { MatchResultPdfExportModal } from "./MatchResultPdfExportModal";
import { matchResultPdfSupportsMode } from "@/lib/match-result-pdf-model";
import { SelectedAgentsPanel } from "./SelectedAgentsPanel";
import { OrganizationalRecordBadge } from "./OrganizationalRecordBadge";
import { CompanyRankingStep } from "./CompanyRankingStep";
import { useOpenedMatchResult } from "./use-opened-match-result";
import { AutoMatchRunsBox } from "./auto-match/AutoMatchRunsBox";
import { useAutoAddedMarks } from "./auto-match/use-auto-added-marks";
import { useHasFeature } from "@/auth/access-features";
import { FEATURE_CATALOG } from "@/auth/feature-catalog";
import { GapListingPlanner } from "./GapListingPlanner";
import { buildGapRequirements, gapPlanIdentity, hasPlannableGaps } from "./gap-listing-plan";
import type { AutoAddedMark, AutoAddition } from "./auto-match/auto-match-runs";
import { useResultSections } from "./use-result-sections";
import { ResultSectionPicker } from "./ResultSectionPicker";
import {
  RESULT_SECTION_IDS,
  isSectionVisible,
  getResultSectionLabels,
  type ResultSectionId,
} from "./result-sections";
import { DEFAULT_REQUIREMENT_LENIENCY, parseDesign } from "./design-codec";
import { parseTeamResult } from "./match-result-codec";
import {
  UNKNOWN_MATCH_MODE,
  assertUnhandledMode,
  resolveMatchMode,
  type UiMatchMode,
} from "./match-mode";
import type { MatchData } from "./match-design-types";
import type { MatchResultListItem, UseMatchEditorResult } from "./use-match-editor";

function teamSizeFromSnapshot(design: MatchResultListItem["design"]): number | null {
  try {
    return parseDesign(design).teamSize;
  } catch {
    return null;
  }
}
import {
  CandidateRankingPanel,
  DataQualityHintsPanel,
  FactsAndSkillsBodyWithViewToggle,
  InfeasibleBanner,
  MatchAnalysisPanel,
  PersonAvatarsRow,
  PowerClustersPanel,
  ProfileStructurePanel,
  SkillStructurePanel,
  SolverErrorBanner,
  TeamPowersBody,
  SkillMatrixBody,
  PowersListPanel,
  useTeamResultView,
  type ResultTeamLike,
} from "./ResultPanel";
import { OperationProgressView } from "@/components/common/OperationProgressView";
import { PreSolveState } from "./PreSolveState";
import { buildPowerColumnGroups, buildRequirementAnalysis, buildWishFit, DEFAULT_RANK_THRESHOLDS } from "./match-result-derive";
import type { RankSortBy, RankThresholds } from "./match-result-derive";

const NO_ADDITIONS: readonly AutoAddition[] = [];

// Der vorhandene Neulauf (PreSolveState, "Kandidaten ranken") steht über der Laufliste; "Neu matchen"
// führt dorthin, statt selbst einen vollen Lauf zu starten, der an dessen Bereitschaftsprüfung vorbeiginge.
function focusRerun() {
  const button = document.getElementById("match-solve");
  button?.scrollIntoView({ behavior: "smooth", block: "center" });
  button?.focus({ preventScroll: true });
}


interface ResultStepProps {
  data: MatchData;
  editor: UseMatchEditorResult;
  isReview: boolean;
  // The workflow's "back to the pool step" button. It shares whichever floating
  // row the surface already has rather than getting its own sticky one: two
  // sticky rows at the same offset look level while scrolling, then drift a row
  // apart once the page bottoms out and both settle into normal flow.
  backSlot?: ReactNode;
}

/**
 * The result step of a match — whichever kind of match it is.
 *
 * A DISPATCHER, and it has to be one: the company arm is a different SCREEN, not a different panel
 * inside the person one. `PersonResultStep` below opens with a dozen hooks that exist for a person
 * result (the ?result= restore, the solver draft, the team normalisation, the save preview), and a
 * fork placed after them would run all of that for a match that has no team at all — while a fork
 * placed before them would be a conditional hook call. So the question is asked here, before either
 * screen mounts.
 *
 * The mode's own table answers it (`resultSurface`), which is why a fifth mode cannot land on the
 * person screen by omission: it has to name its surface, and tsc asks.
 */
export function ResultStep(props: ResultStepProps) {
  const solveProgress = props.editor.solverLoading
    ? <OperationProgressView progress={props.editor.solverProgress} /> : null;
  const surface = resultSurface(resolveMatchMode(props.data.matchMode));
  if (surface === "companyRanking") {
    return (
      <CompanyRankingStep data={props.data} editor={props.editor} backSlot={props.backSlot} solveProgress={solveProgress} />
    );
  }
  return <PersonResultStep {...props} solveProgress={solveProgress} />;
}

function PersonResultStep({ data, editor, isReview, backSlot, solveProgress }: ResultStepProps & { solveProgress: ReactNode }) {
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const intl = useIntl();
  const [saveOpen, setSaveOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  // Sichtzustand der Rangliste: Schwellen und Sortierachse. Liegt hier und nicht im Panel, weil
  // der PDF-Export ihn mitnimmt - die Achse veraendert die A/B/C-Baender selbst, ein Export mit
  // Vorgabewerten wuerde dem Bildschirm widersprechen, von dem er ausgeloest wurde.
  const [rankThresholds, setRankThresholds] = useState<RankThresholds>(DEFAULT_RANK_THRESHOLDS);
  const [rankSortBy, setRankSortBy] = useState<RankSortBy>("smycles");
  const [confirmDiscardOpen, setConfirmDiscardOpen] = useState(false);
  // Welches gespeicherte Ergebnis offen ist, samt ?result=-Spiegelung und Cold-Restore nach
  // Browser-Zurück: eine Implementierung für beide Result-Steps.
  const {
    openedResult,
    openError,
    open: openSavedResult,
    close: closeSavedResult,
  } = useOpenedMatchResult(editor.openResult);
  const canEditContent = editor.canEditResults;

  const { hiddenSections, saving: sectionsSaving, error: sectionsError, change: handleSectionsChange } =
    useResultSections(openedResult, editor.updateResultSections);

  const activeTeams: ResultTeamLike[] = openedResult
    ? normaliseResultTeams(openedResult.teams)
    : editor.solverResult
      ? normaliseResultTeams(editor.solverResult.teams)
      : [];
  // Anforderungspruefung des ANGEZEIGTEN Ergebnisses: gespeicherte Results tragen ihren
  // Design-Snapshot, der Live-Lauf nutzt den aktuellen Editor-Stand. Treibt die amber
  // Kulanz-Markierung im Ranking.
  // When viewing a saved result, design-derived inputs must come from the result's OWN snapshot,
  // not the live editor — otherwise an old team gets scored against the current settings (e.g. the
  // Power-Fit / "Wunsch-Niveau" overlay). matchMode is exempt: it lives on the Match column and is
  // immutable (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4), so live `data.matchMode` is always correct.
  const openedDesign = openedResult ? parseDesign(openedResult.design) : null;
  const activePowerScoreLens = openedResult
    ? (openedDesign?.powerScoreLens ?? DEFAULT_POWER_SCORE_LENS)
    : data.powerScoreLens;
  const requirementLeniency = openedDesign
    ? (openedDesign.requirementLeniency ?? DEFAULT_REQUIREMENT_LENIENCY)
    : data.requirementLeniency;
  const activeElementWeights = openedDesign
    ? openedDesign.teamProfile?.elementWeights
    : data.teamProfile?.elementWeights;
  const hasResult =
    activeTeams.length > 0 && activeTeams.some((t) => t.persons.length > 0);
  const team = hasResult ? activeTeams[0] : null;
  const autoAddedMarks = useAutoAddedMarks(editor.matchId, team?.autoAdded ?? NO_ADDITIONS);
  const exportResult = openedResult ?? (hasResult ? editor.solverResult : null);
  // genesis answers a result whose AI judgement saw organisational-record entries only to record
  // readers (GENESIS-0060). Whoever sees it here holds the capability; the badge explains why
  // colleagues without it see a shorter list.
  const usesOrganizationalRecord = openedResult
    ? openedResult.usesOrganizationalRecord === true
    : hasResult && editor.solverResult?.usesOrganizationalRecord === true;
  const isInfeasible =
    !openedResult &&
    editor.solverResult !== null &&
    !editor.solverLoading &&
    !editor.solverError &&
    !hasResult;
  // Named because the floating row below now renders for the back button too,
  // so the save button needs its own guard rather than the whole row's.
  const canSaveResult =
    canEditContent &&
    editor.solverResult !== null &&
    !editor.solverResultSaved &&
    !openedResult &&
    !isInfeasible;

  // Genesis carries the infeasibility class + relax-MUST culprits in the (empty)
  // team's opaque result blob; null when the backend returned no team node at all
  // (→ generic banner).
  const infeasibility = isInfeasible
    ? parseTeamResult(activeTeams[0]?.result ?? null).infeasibility
    : null;
  useAssistantContext(
    team || isInfeasible
      ? [
          {
            description: "the match-result view currently open",
            value: [
              openedResult
                ? `saved result "${openedResult.name || "unnamed"}"`
                : "newly calculated, not-yet-saved result",
              `match mode ${resolveMatchMode(data.matchMode)}`,
              team ? `${team.persons.length} candidates in the result` : "no feasible result",
              openedResult?.notes ? `result notes: ${openedResult.notes}` : null,
              infeasibility?.reason ? `solver reason: ${infeasibility.reason}` : null,
            ]
              .filter(Boolean)
              .join("; ")
              .slice(0, 1_900),
          },
        ]
      : null,
  );

  async function handleOpenResult(id: string) {
    // Der Sprung nach oben bleibt hier: der Hook beantwortet "welches Ergebnis", nicht "wohin
    // scrollt die Seite danach".
    if (await openSavedResult(id)) window.scrollTo({ top: 0, behavior: "smooth" });
  }
  async function handleSaveResult({
    name,
    notes,
    hiddenSections,
  }: {
    name: string;
    notes?: string;
    hiddenSections?: string[];
  }) {
    // Ein Aufruf: kept, Name, Notiz und die Abschnittsauswahl gehen zusammen an
    // updateMatchResult. Zwei Aufrufe haetten einen Zwischenstand erzeugt, in dem das Ergebnis
    // gespeichert ist und die Auswahl noch nicht.
    const resultId = editor.solverResult?.id ?? null;
    await editor.saveResult({ name, notes, hiddenSections });
    setSaveOpen(false);
    if (resultId) await openSavedResult(resultId);
  }
  function handleBackToPreSolve() {
    const hasUnsavedTransient =
      Boolean(editor.solverResult) && !editor.solverResultSaved && !openedResult && hasResult;
    if (hasUnsavedTransient) {
      setConfirmDiscardOpen(true);
      return;
    }
    backToPreSolve();
  }

  function backToPreSolve() {
    setConfirmDiscardOpen(false);
    closeSavedResult();
    editor.clearSolverResult();
  }
  return (
    <div className="space-y-6">
      <ConfirmDialog
        id="match-discard-result"
        open={confirmDiscardOpen}
        title={
          <FormattedMessage
            id="match.result.discardUnsavedConfirm.title"
            defaultMessage="Ungespeichertes Ergebnis verwerfen?"
          />
        }
        message={
          <FormattedMessage
            id="match.result.discardUnsavedConfirm.message"
            defaultMessage="Das berechnete Ergebnis wurde noch nicht gespeichert und geht beim Zurückgehen verloren."
          />
        }
        confirmLabel={
          <FormattedMessage id="match.result.discardUnsavedConfirm.confirm" defaultMessage="Verwerfen" />
        }
        onConfirm={backToPreSolve}
        onCancel={() => setConfirmDiscardOpen(false)}
      />
      {exportOpen && exportResult ? (
        <MatchResultPdfExportModal
          open
          onClose={() => setExportOpen(false)}
          data={data}
          result={exportResult}
          rankThresholds={rankThresholds}
          rankSortBy={rankSortBy}
        />
      ) : null}
      <div>
        {openedResult && (
          <div className="smycles-field-label mb-2">
            <FormattedMessage
              id="match.result.savedResultLabel"
              defaultMessage="Gespeichertes Ergebnis · {name}"
              values={{
                name:
                  openedResult.name ??
                  intl.formatMessage({
                    id: "match.result.unnamed",
                    defaultMessage: "ohne Namen",
                  }),
              }}
            />
          </div>
        )}
        {usesOrganizationalRecord ? (
          <div className="mb-2">
            <OrganizationalRecordBadge id="result-organizational-record-badge" />
          </div>
        ) : null}
        {/* Metadaten des Laufs stehen zusammen ganz oben, wie das Gespeichert-Label darueber:
            eine smycles-field-label-Zeile UNTER den Buttons hing sonst als Eyebrow ueber dem Untertitel,
            den sie nicht beschriftet. */}
        {hasResult ? <PowerScoreLensSummary lens={activePowerScoreLens} /> : null}
        {(hasResult || isInfeasible) && (
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              id="result-close"
              onClick={handleBackToPreSolve}
            >
              <FormattedMessage
                id="match.result.backToSelection"
                defaultMessage="← Schließen"
              />
            </Button>
            {exportResult && matchResultPdfSupportsMode(resolveMatchMode(data.matchMode)) ? (
              <Button variant="secondary" size="sm" onClick={() => setExportOpen(true)}>
                <FormattedMessage id="match.pdf.openExport" defaultMessage="PDF export" />
              </Button>
            ) : null}
            {openedResult && team && editor.canEditResults && resultSurface(resolveMatchMode(data.matchMode)) === "teamDashboard" ? (
              <div className="ml-auto flex">
                <Button variant="secondary" size="sm" onClick={() => setSectionsOpen((open) => !open)} aria-expanded={sectionsOpen}>
                  <FormattedMessage id="match.result.sectionPicker.trigger" defaultMessage="Abschnitte" />
                </Button>
              </div>
            ) : null}
          </div>
        )}
        {/* Pre-solve needs no subtitle: the PreSolveState panel below is
            self-describing (eyebrow + heading + action). A subtitle only earns
            its place over a result/saved view, which has no such panel. */}
        {(openedResult || isReview) && (
          <p className="max-w-2xl text-smycles-body text-ink">
            {openedResult
              ? intl.formatMessage({
                  id: "match.result.savedSubtitle",
                  defaultMessage: "Du siehst ein gespeichertes Ergebnis dieses Matches.",
                })
              : intl.formatMessage({
                  id: "match.result.reviewSubtitle",
                  defaultMessage:
                    "Optimiert auf dein Anforderungsprofil – transparent, erklärbar, exportierbar.",
                })}
          </p>
        )}
      </div>

      {editor.solverError && !editor.solverLoading && (
        <SolverErrorBanner error={editor.solverError} onRetry={() => void editor.runSolver()} />
      )}

      {isInfeasible && (
        <InfeasibleBanner
          infeasibility={infeasibility}
          onManagePeople={() => {
            editor.clearSolverResult();
            editor.setStepId("people");
          }}
          onAdjustRequirements={() => {
            editor.clearSolverResult();
            editor.setStepId("challenge");
          }}
        />
      )}

      {openError && (
        <div className="box bg-rose-50 p-4 text-smycles-meta text-rose-700">{openError}</div>
      )}

      {team ? (
        <>
          {solveProgress}
          <PostSolveDashboard
          team={team}
          matchId={editor.matchId}
          resultId={openedResult?.id ?? editor.solverResult?.id ?? null}
          data={data}
          requirementLeniency={requirementLeniency}
          elementWeights={activeElementWeights}
          hiddenSections={openedResult ? hiddenSections : undefined}
          // Nur am gespeicherten Ergebnis bedienbar. Der frisch gerechnete Entwurf bekommt
          // keinen Schalter: ihn ersetzt der naechste Solve, eine dort gesetzte Auswahl waere
          // sofort wieder weg. Festgelegt wird sie stattdessen im Speichern-Dialog.
          onHiddenSectionsChange={openedResult && editor.canEditResults ? handleSectionsChange : undefined}
          sectionsOpen={sectionsOpen}
          sectionsSaving={sectionsSaving}
          sectionsError={sectionsError}
          rankThresholds={rankThresholds}
          onRankThresholdsChange={setRankThresholds}
          rankSortBy={rankSortBy}
          onRankSortByChange={setRankSortBy}
          teamFitBasis={openedResult ? openedDesign?.recruitingTeamFit ?? null : data.recruitingTeamFit ?? null}
          autoAdded={autoAddedMarks}
          />
        </>
      ) : (
        <>
          {!isInfeasible && <PreSolveState editor={editor} />}
          {/* Under the box that started the run, where the eye already is after the click. */}
          {solveProgress}
          <ResultsHistory
            editor={editor}
            onOpen={handleOpenResult}
            openedResultId={openedResult?.id ?? null}
          />
          <AutoMatchRunsBox
            matchId={editor.matchId}
            latestSavedResultAt={editor.results[0]?.createdAt ?? null}
            onRematch={focusRerun}
          />
        </>
      )}

      {(backSlot || canSaveResult) && (
        <FloatingActionRow>
          {backSlot ?? <span />}
          {canSaveResult ? (
            <Button
              variant="primary"
              id="result-save"
              onClick={() => setSaveOpen(true)}
              size="floating"
              className="pointer-events-auto"
            >
              <FormattedMessage id="match.result.saveResult" defaultMessage="Ergebnis speichern" />
            </Button>
          ) : (
            <span />
          )}
        </FloatingActionRow>
      )}

      {saveOpen && (
        <SaveMatchModal
          defaultName={data.matchTitle || data.projectName}
          defaultNotes=""
          onClose={() => setSaveOpen(false)}
          onSave={handleSaveResult}
          sectionChoice={{ sectionIds: RESULT_SECTION_IDS, labels: getResultSectionLabels(
            team?.persons.length ?? 0,
            buildWishFit(parseTeamResult(team?.result).powers ?? null, activeElementWeights).weighted != null,
          ) }}
          previewPersons={team?.persons ?? []}
          previewMeta={{
            teamSize: data.teamSize,
            candidateCount: editor.solverResult?.candidateCount ?? null,
            requirementsCount: data.conditions.length,
            agenticAi: Boolean(data.agenticAi),
            // Immutable mode is the source of truth (@adr SPARK-0029-match-three-case-model-optimize-assess-rank@4); fall back to the assessOnly flag only
            // for legacy matches with no matchMode (pre-#159), which never had a RANK case.
            // Resolved on the way in, so the preview labels an unknown token as unknown.
            matchMode: resolveMatchMode(data.matchMode ?? (editor.assessOnly ? "ASSESS" : "OPTIMIZE")),
          }}
        />
      )}
    </div>
  );
}

function PowerScoreLensSummary({ lens }: { lens: PowerScoreLens }) {
  const intl = useIntl();

  return (
    <div className="smycles-field-label mb-2">
      {intl.formatMessage({ id: "grow.powerLibrary.powerScore", defaultMessage: "Power Score" })}
      {" · "}
      {intl.formatMessage({ id: POWER_SCORE_LENS_LABEL_IDS[lens] })}
    </div>
  );
}

// Which result surface a mode renders. A table, not a `=== "RANK"` check with an implicit
// everything-else: a new mode has to say which surface it shows, and tsc asks here.
//
// Read TWICE, at two different altitudes, and deliberately the same table both times: ResultStep
// above uses it to pick the SCREEN (company ranking, or the person step), and PostSolveDashboard
// below uses it to pick the person screen's own panel (candidate ranking, or the team dashboard).
// One table means the two cannot disagree about what a mode shows.
type ResultSurface = "candidateRanking" | "teamDashboard" | "companyRanking";

function resultSurface(mode: UiMatchMode): ResultSurface {
  switch (mode) {
    case "RANK":
      return "candidateRanking";
    case "RANK_COMPANIES":
      return "companyRanking";
    // UNKNOWN mit dabei: das Team-Dashboard ist die einzige Oberflaeche, die ohne Modus-Wissen
    // auskommt (sie liest nur den Result-Blob) — unveraendert zum Verhalten vor dem Refactor.
    case "OPTIMIZE":
    case "ASSESS":
    case UNKNOWN_MATCH_MODE:
      return "teamDashboard";
    default:
      return assertUnhandledMode(mode);
  }
}

function PostSolveDashboard({
  team,
  matchId,
  resultId,
  data,
  requirementLeniency,
  elementWeights,
  hiddenSections,
  onHiddenSectionsChange,
  sectionsSaving,
  sectionsOpen,
  sectionsError,
  rankThresholds,
  onRankThresholdsChange,
  rankSortBy,
  onRankSortByChange,
  teamFitBasis,
  autoAdded,
}: {
  team: ResultTeamLike;
  matchId: string;
  /** Das angezeigte Ergebnis; ein anderes setzt den Stellenplan zurück. */
  resultId: string | null;
  data: MatchData;
  requirementLeniency: MatchData["requirementLeniency"];
  elementWeights: Record<string, number> | undefined;
  /** Ausgeblendete Abschnitte aus `MatchResult.hiddenSections`; null/leer heisst "alles zeigen". */
  hiddenSections?: readonly string[] | null;
  /**
   * Fehlt der Handler, ist die Auswahl gar nicht bedienbar — so bleibt der frisch gerechnete
   * Entwurf ohne Schalter. Die Einstellung gehoert an ein GESPEICHERTES Ergebnis: der Entwurf
   * wird vom naechsten Solve ersetzt, eine dort gesetzte Auswahl waere sofort wieder weg.
   */
  onHiddenSectionsChange?: (next: string[]) => void;
  /** Fehlschlag beim Speichern der Auswahl; die Anzeige ist dann schon zurueckgefallen. */
  sectionsError?: string | null;
  sectionsSaving?: boolean;
  sectionsOpen: boolean;
  rankThresholds: RankThresholds;
  onRankThresholdsChange: (next: RankThresholds) => void;
  rankSortBy: RankSortBy;
  onRankSortByChange: (next: RankSortBy) => void;
  teamFitBasis: RecruitingTeamFitBasis | null;
  autoAdded: ReadonlyMap<string, AutoAddedMark>;
}) {
  const view = useTeamResultView(team);
  const mode = resolveMatchMode(data.matchMode);
  // Fachkompetenz-Disklosure nur, wenn der Blob überhaupt ein Skill-Inventar trägt (@external-adr GENESIS-0032-match-result-blob-carries-expertise-atoms):
  // sonst bliebe die Matrix leer (RANK, Alt-Blobs, Match ohne geladene Fachkompetenzen).
  const hasSkillInventory = (view.factSkill?.teamMembers ?? []).some(
    (m) => (m.expertises?.length ?? 0) > 0,
  );
  // Der Zustand "welche Cluster-Spalten sind offen" lebt hier, nicht in der Matrix - der
  // Pill-Switch im Disclosure-Header ("Cluster"/"Alle 30 Powers") und die einzelnen
  // Spalten-Klicks in der Matrix teilen sich dasselbe Set. So kann der Pill seinen Zustand aus
  // der tatsächlichen Spaltenlage ableiten: klappt man manuell alle Spalten auf, springt der
  // Pill von selbst auf "Alle 30 Powers", statt stumm falsch anzuzeigen.
  const powerColumnGroups = useMemo(() => buildPowerColumnGroups(view.powers), [view.powers]);
  const [openPowerColumns, setOpenPowerColumns] = useState<Set<string>>(new Set());
  const allPowerColumnsOpen =
    powerColumnGroups.length > 0 && powerColumnGroups.every((g) => openPowerColumns.has(g.key));
  const powersMatrixScope: "cluster" | "element" = allPowerColumnsOpen ? "element" : "cluster";
  const setPowersMatrixScope = (next: "cluster" | "element") =>
    setOpenPowerColumns(next === "element" ? new Set(powerColumnGroups.map((g) => g.key)) : new Set());
  const togglePowerColumn = (key: string) =>
    setOpenPowerColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  // "Team Fokus" (eine Kompetenz, alle Traeger auf der Stufen-Achse) vs. "Skill Fokus" (eine
  // Stufe, alle Kompetenzen, die das Team dort haelt) - zwei Pivots derselben Rohdaten, kein
  // gemeinsamer Zustand mit der Matrix selbst noetig (anders als powersMatrixScope), lebt aber aus
  // demselben Grund hier: der Pill sitzt im Disclosure-Header, eine Ebene ueber der Matrix. Default
  // "team": die Stufen-Achse pro Kompetenz ist die Ansicht, die zuerst entstand und am meisten
  // genutzt wird.
  const [skillMatrixFocus, setSkillMatrixFocus] = useState<"skill" | "team">("team");

  // Aus Lücken Stellen planen (spark#1237): nur wo es ein Team mit Lücken gibt, also Optimieren und
  // Bewerten, und nur mit Ausschreibungen. Die Lücken sind die des angezeigten Teams; ein anderes
  // Ergebnis oder ein anderer Anforderungsstand setzt den Planer zurück (gapPlanIdentity als key).
  const canPlanListings = useHasFeature(FEATURE_CATALOG.shareListings.key) && (mode === "OPTIMIZE" || mode === "ASSESS");
  const gapRequirements = useMemo(
    () => (canPlanListings ? buildGapRequirements(buildRequirementAnalysis(view.factSkill)) : []),
    [canPlanListings, view.factSkill],
  );
  const [planningListings, setPlanningListings] = useState(false);
  const gapAction = hasPlannableGaps(gapRequirements) ? (
    <Button variant="secondary" size="compact" onClick={() => setPlanningListings(true)}>
      <FormattedMessage id="match.gapListings.open" defaultMessage="Aus Lücken Stellen planen" />
    </Button>
  ) : undefined;

  // Ranken-Modus: nur das A/B/C-Ranking, keine Team-/Power-Boxen —
  // die sind auf Team-Komposition zugeschnitten und fuers Kandidaten-Ranking Rauschen.
  // Erschoepfend statt `=== "RANK"`: welche Ergebnis-Oberflaeche ein Modus rendert, ist eine
  // Entscheidung pro Modus, und kein Modus soll das Team-Dashboard erben, nur weil er hier nicht
  // genannt ist. Bewusst NICHT an ranksCandidates(mode) gehaengt: dieses Panel ist personen-
  // geformt, ein anderer rankender Modus braucht sein eigenes.
  // "companyRanking" wird hier nicht behandelt: ResultStep verzweigt eine Unternehmens-Rangliste vor
  // diesem Personen-Bildschirm, dieses Panel kann sie also nicht erreichen. Ein zweiter Arm hier
  // waere toter Code, der behauptet, ein Rueckfall zu sein.
  if (resultSurface(mode) === "candidateRanking") {
    return (
      <div className="space-y-6">
        <CandidateRankingPanel
          view={view}
          persons={team.persons}
          elementWeights={elementWeights}
          requirementLeniency={requirementLeniency}
          thresholds={rankThresholds}
          onThresholdsChange={onRankThresholdsChange}
          sortBy={rankSortBy}
          onSortByChange={onRankSortByChange}
          teamFitBasis={teamFitBasis}
          autoAdded={autoAdded}
        />
      </div>
    );
  }

  // Die drei Inhalte der Details-Box einzeln. Die Box selbst hat keine eigene Kennung: sie ist
  // eine Huelle, und eine Huelle, die man getrennt abschalten kann, laesst eine eingeschaltete
  // Matrix unsichtbar werden.
  const showRequirements = isSectionVisible("requirements", hiddenSections);
  const showPowerMatrix = isSectionVisible("powerMatrix", hiddenSections);
  // hasSkillInventory bleibt die harte Bedingung: ohne Skill-Inventar im Blob waere die Matrix
  // leer. Die Sichtbarkeit kommt oben drauf, sie ersetzt die Datenfrage nicht.
  const showSkillMatrix = hasSkillInventory && isSectionVisible("skillMatrix", hiddenSections);
  const showDetails = showRequirements || showPowerMatrix || showSkillMatrix;

  const detailsSection = (
    <DetailsSection
      view={view}
      team={team}
      requirementLeniency={requirementLeniency}
      showRequirements={showRequirements}
      showPowerMatrix={showPowerMatrix}
      showSkillMatrix={showSkillMatrix}
      powersMatrixScope={powersMatrixScope}
      setPowersMatrixScope={setPowersMatrixScope}
      openPowerColumns={openPowerColumns}
      togglePowerColumn={togglePowerColumn}
      skillMatrixFocus={skillMatrixFocus}
      setSkillMatrixFocus={setSkillMatrixFocus}
    />
  );

  // Die Boxen als Liste statt als acht aufeinanderfolgende Bloecke: nur so kann die
  // Sichtbarkeit an EINER Stelle entscheiden, welche davon erscheinen, und der Speichern-Dialog
  // dieselbe Reihenfolge beschriften. Die Kennungen sind ein Datenvertrag mit Genesis
  // (`MatchResult.hiddenSections`), siehe result-sections.ts.
  const sections: Array<{ id: ResultSectionId | "powers"; node: ReactNode }> = [
    { id: "dataQuality", node: <DataQualityHintsPanel view={view} /> },
    {
      id: "team",
      node: (
        <div id="result-team-box" className="box bg-surface p-6">
          <BoxHeader
            title={
              <span id="result-team-count">
                <FormattedMessage
                  id="match.result.teamHeader"
                  defaultMessage="Team · {count, plural, one {# Person} other {# Personen}}"
                  values={{ count: team.persons.length }}
                />
              </span>
            }
          />
          <PersonAvatarsRow
            persons={team.persons}
            view={view}
            elementWeights={elementWeights}
            requirementLeniency={requirementLeniency}
            assessOnly={mode === "ASSESS"}
          />
        </div>
      ),
    },
    // Direkt hinter dem Roster das Urteil. Die Charakter-Box davor ist weg: ihre eine
    // bleibende Aussage - die Neigung beider Achsen - traegt jetzt der Kopf der
    // Match-Analyse, und "was fuer ein Team" vor "taugt es" war eine Box Verzoegerung
    // vor der Antwort, nach der der Nutzer gekommen ist (spark #236).
    { id: "analysis", node: <MatchAnalysisPanel view={view} persons={team.persons} gapAction={gapAction} /> },
    // Zwei Powers-Boxen: die Liste (sortierbar nach Wunsch oder Team-Score) und die Form.
    // Vorher drei - "Top Powers" und "Wichtigste Powers" zeigten dieselbe Zeile und
    // unterschieden sich nur in der Sortierung, also sind sie eine Liste (spark #236).
    // Each box has its own headline and toggle; a remaining box uses the full row.
    {
      id: "powers",
      node: (
        <div className={`grid grid-cols-1 gap-4 ${isSectionVisible("topPowers", hiddenSections) && isSectionVisible("powerRadar", hiddenSections) ? "@lg:grid-cols-2" : ""}`}>
          {isSectionVisible("topPowers", hiddenSections) ? (
            <PowersListPanel view={view} persons={team.persons} elementWeights={elementWeights} />
          ) : null}
          {isSectionVisible("powerRadar", hiddenSections) ? (
            <PowerClustersPanel view={view} elementWeights={elementWeights} assessOnly={mode === "ASSESS"} />
          ) : null}
        </div>
      ),
    },
    { id: "profileStructure", node: <ProfileStructurePanel view={view} /> },
    { id: "skillStructure", node: <SkillStructurePanel view={view} /> },
    // Agents haengen weiter an `agenticAi`: das ist keine Anzeigepraeferenz, sondern die Frage,
    // ob es ueberhaupt Agents gibt. Der Sichtbarkeitsschalter kommt oben drauf, er ersetzt sie nicht.
    ...(data.agenticAi
      ? [{ id: "agents" as const, node: <SelectedAgentsPanel agents={data.agents} /> }]
      : []),
  ];


  return (
    <div className="space-y-6">
      {sectionsOpen && onHiddenSectionsChange ? (
        <>
          <ResultSectionPicker
            sectionIds={RESULT_SECTION_IDS}
            labels={getResultSectionLabels(team.persons.length, buildWishFit(view.powers, elementWeights).weighted != null)}
            hidden={hiddenSections}
            onChange={onHiddenSectionsChange}
            disabled={sectionsSaving}
          />
          {sectionsError ? (
            <div role="alert" className="box bg-rose-50 p-4 text-smycles-meta text-rose-700">{sectionsError}</div>
          ) : null}
        </>
      ) : null}

      {sections
        .filter((s) => s.id === "powers"
          ? isSectionVisible("topPowers", hiddenSections) || isSectionVisible("powerRadar", hiddenSections)
          : isSectionVisible(s.id, hiddenSections))
        .map((s) => (
          <Fragment key={s.id}>{s.node}</Fragment>
        ))}

      {showDetails ? detailsSection : null}

      {planningListings && gapAction ? (
        <GapListingPlanner
          key={gapPlanIdentity(resultId, gapRequirements)}
          matchId={matchId}
          gaps={gapRequirements}
          description={data.projectDescription}
          onClose={() => setPlanningListings(false)}
        />
      ) : null}
    </div>
  );
}

/** Der Details-Block, aus dem Dashboard herausgezogen, damit die Abschnittsliste lesbar bleibt. */
function DetailsSection({
  view,
  team,
  requirementLeniency,
  showRequirements,
  showPowerMatrix,
  showSkillMatrix,
  powersMatrixScope,
  setPowersMatrixScope,
  openPowerColumns,
  togglePowerColumn,
  skillMatrixFocus,
  setSkillMatrixFocus,
}: {
  view: ReturnType<typeof useTeamResultView>;
  team: ResultTeamLike;
  requirementLeniency: MatchData["requirementLeniency"];
  showRequirements: boolean;
  showPowerMatrix: boolean;
  showSkillMatrix: boolean;
  powersMatrixScope: "cluster" | "element";
  setPowersMatrixScope: (next: "cluster" | "element") => void;
  openPowerColumns: Set<string>;
  togglePowerColumn: (key: string) => void;
  skillMatrixFocus: "skill" | "team";
  setSkillMatrixFocus: (next: "skill" | "team") => void;
}) {
  return (
    <div className="box bg-surface p-5">
        <BoxHeader title={<FormattedMessage id="match.result.detailsHeader" defaultMessage="Details" />} />
        <div className="space-y-2">
          {showRequirements && (
            <FactsAndSkillsBodyWithViewToggle
              view={view}
              persons={team.persons}
              requirementLeniency={requirementLeniency}
            />
          )}
          {showPowerMatrix && (
          <Disclosure
            title={<FormattedMessage id="match.result.teamPowers" defaultMessage="Power-Scorecard-Matrix" />}
            headerAction={
              <SegmentedControl
                size="sm"
                value={powersMatrixScope}
                onChange={setPowersMatrixScope}
                options={[
                  {
                    value: "cluster",
                    label: <FormattedMessage id="match.result.radarScopeCluster" defaultMessage="Cluster" />,
                  },
                  {
                    value: "element",
                    label: (
                      // Die Zahl gehoert in die Nachricht (dieselbe id traegt sie am Radar auch):
                      // ohne `count` fand react-intl keinen Wert fuer den Platzhalter und fiel auf
                      // den deutschen defaultMessage zurueck - in der englischen Oberflaeche stand
                      // deshalb "Alle 30 Powers".
                      <FormattedMessage
                        id="match.result.radarScopeElement"
                        defaultMessage="Alle {count} Powers"
                        values={{ count: buildPowerColumnGroups(view.powers).flatMap((g) => g.elements).length }}
                      />
                    ),
                  },
                ]}
              />
            }
          >
            <TeamPowersBody
              view={view}
              persons={team.persons}
              openColumns={openPowerColumns}
              onToggleColumn={togglePowerColumn}
            />
          </Disclosure>
          )}
          {showSkillMatrix && (
            <Disclosure
              title={<FormattedMessage id="match.result.skillMatrix" defaultMessage="Fachkompetenz-Matrix" />}
              headerAction={
                <SegmentedControl
                  size="sm"
                  value={skillMatrixFocus}
                  onChange={setSkillMatrixFocus}
                  options={[
                    {
                      value: "team",
                      label: (
                        <FormattedMessage id="match.result.skillMatrixFocusTeam" defaultMessage="Team Fokus" />
                      ),
                    },
                    {
                      value: "skill",
                      label: (
                        <FormattedMessage id="match.result.skillMatrixFocusSkill" defaultMessage="Skill Fokus" />
                      ),
                    },
                  ]}
                />
              }
            >
              <SkillMatrixBody view={view} persons={team.persons} focus={skillMatrixFocus} />
            </Disclosure>
          )}
        </div>
    </div>
  );
}

function ResultsHistory({
  editor,
  onOpen,
  openedResultId,
}: {
  editor: UseMatchEditorResult;
  onOpen: (id: string) => Promise<void>;
  openedResultId: string | null;
}) {
  const intl = useIntl();
  const [pendingDelete, setPendingDelete] = useState<MatchResultListItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  if (editor.resultsLoading && editor.results.length === 0) {
    return (
      <div className="text-smycles-label text-ink-muted">
        <span id="saved-results-loading">
          <FormattedMessage id="match.result.historyLoading" defaultMessage="Gespeicherte Ergebnisse laden …" />
        </span>
      </div>
    );
  }
  if (editor.results.length === 0) return null;

  const canDelete = editor.canEditResults;

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await editor.deleteResult(pendingDelete.id);
      setPendingDelete(null);
    } catch (e) {
      setDeleteError(userErrorMessage(intl, e));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div id="saved-results-box" className="box bg-surface p-5">
      <BoxHeader
        title={<FormattedMessage id="match.result.historyTitle" defaultMessage="Gespeicherte Ergebnisse" />}
        right={<span className="text-smycles-label text-ink-muted tabular-nums">{editor.results.length}</span>}
      />
      <ul className="divide-y divide-ink/5">
        {editor.results.map((r) => {
          const isActive = r.id === openedResultId;
          const teamSize = teamSizeFromSnapshot(r.design);
          const meta = [
            r.createdAt ? formatDate(r.createdAt, intl.locale) : null,
            // Teamgröße ist ein Team-Zusammenstellungs-Wert (nur Optimize). Im Assess/Rank-Modus
            // (assessOnly) werden einzelne Kandidaten gegen eine Stelle bewertet, dort ist sie
            // bedeutungslos und wird ausgeblendet.
            !editor.assessOnly && teamSize && teamSize > 0
              ? intl.formatMessage(
                  { id: "match.result.teamSizeMeta", defaultMessage: "Teamgröße {count}" },
                  { count: teamSize },
                )
              : null,
            intl.formatMessage(
              {
                id: "match.result.candidatesMeta",
                defaultMessage: "{count, plural, one {# Kandidat} other {# Kandidaten}}",
              },
              { count: r.candidateCount ?? 0 },
            ),
            r.createdBy?.formattedName ?? null,
          ]
            .filter(Boolean)
            .join(" · ");
          return (
            <li key={r.id} id={`saved-result-row-${r.id}`} className="flex items-center gap-3 py-2.5">
              <button
                id={`saved-result-open-${r.id}`}
                onClick={() => void onOpen(r.id)}
                className="-mx-2 min-w-0 flex-1 cursor-pointer rounded-md px-2 py-1 text-left transition hover:bg-ink/[0.06]"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <div id={`saved-result-name-${r.id}`} className="truncate text-smycles-meta font-medium">
                    {r.name ??
                      intl.formatMessage({
                        id: "match.result.unnamed",
                        defaultMessage: "ohne Namen",
                      })}
                  </div>
                  {r.usesOrganizationalRecord ? (
                    <OrganizationalRecordBadge id={`saved-result-organizational-record-${r.id}`} insideInteractive />
                  ) : null}
                </div>
                <div className="text-smycles-micro text-ink-muted">{meta}</div>
                {r.notes && (
                  <div className="mt-1 truncate text-smycles-label italic text-ink-muted">{r.notes}</div>
                )}
              </button>
              {isActive && (
                <span className="text-smycles-micro uppercase tracking-[0.2em] text-ink-accent">
                  <FormattedMessage id="match.result.openFlag" defaultMessage="offen" />
                </span>
              )}
              {canDelete && (
                <Button
                  id={`saved-result-delete-${r.id}`}
                  variant="icon"
                  size="sm"
                  onClick={() => {
                    setDeleteError(null);
                    setPendingDelete(r);
                  }}
                  title={intl.formatMessage({
                    id: "match.result.deleteTitle",
                    defaultMessage: "Löschen",
                  })}
                  aria-label={intl.formatMessage(
                    {
                      id: "match.result.deleteAriaLabel",
                      defaultMessage: 'Ergebnis „{name}“ löschen',
                    },
                    {
                      name:
                        r.name ??
                        intl.formatMessage({
                          id: "match.result.unnamed",
                          defaultMessage: "ohne Namen",
                        }),
                    },
                  )}
                >
                  ✕
                </Button>
              )}
            </li>
          );
        })}
      </ul>

      <ConfirmDialog
        id="match-delete-result"
        open={pendingDelete !== null}
        title={intl.formatMessage({
          id: "match.result.deleteConfirmTitle",
          defaultMessage: "Ergebnis löschen?",
        })}
        message={
          pendingDelete && (
            <div className="space-y-2">
              <p>
                <FormattedMessage
                  id="match.result.deleteConfirmBody"
                  defaultMessage='Das gespeicherte Ergebnis <b>„{name}“</b> wird endgültig entfernt. Diese Aktion kann nicht rückgängig gemacht werden.'
                  values={{
                    name:
                      pendingDelete.name ??
                      intl.formatMessage({
                        id: "match.result.unnamed",
                        defaultMessage: "ohne Namen",
                      }),
                    b: (chunks) => <span className="font-medium text-ink">{chunks}</span>,
                  }}
                />
              </p>
              <p className="text-smycles-label text-ink-muted">
                {[
                  pendingDelete.createdAt ? formatDate(pendingDelete.createdAt, intl.locale) : null,
                  (() => {
                    const ts = teamSizeFromSnapshot(pendingDelete.design);
                    return !editor.assessOnly && ts && ts > 0
                      ? intl.formatMessage(
                          {
                            id: "match.result.teamSizeMeta",
                            defaultMessage: "Teamgröße {count}",
                          },
                          { count: ts },
                        )
                      : null;
                  })(),
                  intl.formatMessage(
                    {
                      id: "match.result.candidatesMeta",
                      defaultMessage: "{count, plural, one {# Kandidat} other {# Kandidaten}}",
                    },
                    { count: pendingDelete.candidateCount ?? 0 },
                  ),
                  pendingDelete.createdBy?.formattedName ?? null,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
              {deleteError && (
                <p role="status" className="rounded-md bg-red-50 px-3 py-2 text-smycles-meta text-red-700">
                  {deleteError}
                </p>
              )}
            </div>
          )
        }
        confirmLabel={
          deleting
            ? intl.formatMessage({ id: "match.result.deleting", defaultMessage: "Lösche…" })
            : intl.formatMessage({
                id: "match.result.deleteFinal",
                defaultMessage: "Endgültig löschen",
              })
        }
        cancelLabel={intl.formatMessage({ id: "common.cancel", defaultMessage: "Abbrechen" })}
        destructive
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!deleting) setPendingDelete(null);
        }}
      />
    </div>
  );
}
